import { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useDatabaseNameQuery, useRestoreBackupMutation } from '../../../hooks/useSystemQueries';
import { parseApiError } from '../../../utils/parseApiError';

/**
 * Restore replaces the whole database. The confirm button only unlocks when
 * the database name is typed — the same check the server makes.
 */
export const RestoreDialog = ({ fileName, onClose }: { fileName: string | null; onClose: () => void }) => {
  const { t } = useTranslation();
  const database = useDatabaseNameQuery(Boolean(fileName));
  const restore = useRestoreBackupMutation();
  const { reset } = restore;
  const [typed, setTyped] = useState('');

  useEffect(() => {
    setTyped('');
    reset();
  }, [fileName, reset]);

  const name = database.data?.name;
  const matches = Boolean(name) && typed === name;

  const submit = async () => {
    if (!fileName || !matches) return;
    try {
      await restore.mutateAsync({ fileName, confirmDatabase: typed });
      onClose();
    } catch {
      // Shown from restore.error.
    }
  };

  return (
    <Dialog
      open={Boolean(fileName)}
      onClose={restore.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('dashboard.system.backups.restoreTitle')}</DialogTitle>
      <DialogContent>
        <p>{t('dashboard.system.backups.restoreWarning', { file: fileName ?? '' })}</p>
        <p className="team-muted">{t('dashboard.system.backups.restoreSafety')}</p>
        <label htmlFor="restore-db-name">{t('dashboard.system.backups.typeName')}</label>
        <input
          id="restore-db-name"
          className="filter-search-input system-restore-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={name}
          autoComplete="off"
          disabled={restore.isPending}
        />
        {restore.isPending && (
          <p className="team-muted" role="status">{t('dashboard.system.backups.restoring')}</p>
        )}
        {restore.error && (
          <p className="role-form-error" role="alert">{parseApiError(restore.error).message}</p>
        )}
      </DialogContent>
      <DialogActions>
        <button type="button" className="secondary-action-btn" onClick={onClose} disabled={restore.isPending}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="danger-action-btn"
          disabled={!matches || restore.isPending}
          onClick={() => void submit()}
        >
          {t('dashboard.system.backups.restoreConfirm')}
        </button>
      </DialogActions>
    </Dialog>
  );
};
